<template>
  <v-app>
    <toolbar v-if="displayToolbar"/>
    <embedded-toolbar v-if="displayEmbeddedToolbar"/>
    <video-conference-overlay />
    <router-view />
    <v-footer v-if="displayFooter" app bottom fixed padless>
      <span v-if="displayCopyright" class="white--grey ml-3 caption">
        &copy; {{ copyrightYear }} 14IP. {{ $t('copyright') }}
      </span>
      <v-spacer></v-spacer>
      <span class="white--grey mr-3 caption">Call Panel {{ version }}</span>
    </v-footer>

    <loader />
    <notify />
    <chat-realtime v-if="canChatsRead"/>
    <dialer v-if="displayDialer"/>
  </v-app>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import Mixins from '@/mixins/app_init'
import Session from '@/api/session'
import events from 'Store/events'
import Loader from '@/components/loader'
import Notify from '@/components/notify'
import Dialer from '@/components/dialer'
import Toolbar from '@/components/toolbar'
import VideoConferenceOverlay from '@/components/video_conferences/overlay'
import ChatRealtime from '@/components/chat/notification'
import API from '@/api'
import VueCookies from 'vue-cookies'
import LocalStorage from 'Store/class/local_storage'
import EmbeddedToolbar from '@/components/toolbar/embedded'
import sipua from '@/store/sipua'
import wakeLock from '@/helpers/wakeLock'

// timeout limit to trigger API data reload after page got
// back focused in milliseconds. Value can be reset with
// environment variable VUE_APP_FOCUS_TIMEOUT. Default value is 30 min
const hiddenTimeout = ~~process.env.VUE_APP_FOCUS_TIMEOUT || (30 * 60 * 1000)

export default {
  name: 'call-panel',
  data () {
    return {
      hiddenTimeStamp: 0,
      version: '',
      copyrightYear: new Date().getFullYear()
    }
  },
  mixins: [Mixins],
  components: {
    Loader,
    Notify,
    Dialer,
    Toolbar,
    VideoConferenceOverlay,
    ChatRealtime,
    EmbeddedToolbar
  },
  mounted () {
    this.version = this.$APPVERSION
    this.appendJitsiExternalAPI()
    this.setUILanguage()
    this.initWakeLock()

    // load static data
    this.initAPI()
    window.addEventListener('online', () => {
      this.initAPI()
    })
    window.addEventListener('offline', () => { events.mask('Connectivity loss...') })
    events.subReloadVoicemails((vmnew) => {
      if (vmnew !== this.sipuaVmNum) {
        this.reloadVmail()
      }
    })
    // periodically update some states after focus back
    window.addEventListener('visibilitychange', (e) => {
      if (document.visibilityState === 'visible') {
        if ((e.timeStamp - this.hiddenTimeStamp) > hiddenTimeout) {
          // trigger api update after focus is back
          this.initBuddies()
        }
      } else {
        this.hiddenTimeStamp = e.timeStamp
      }
    })

    // prevent close tab on active call
    window.addEventListener('beforeunload', (e) => {
      if (this.isOnCall) {
        e.preventDefault()
        const confirm = 'You are on call. Are you sure you want to close and hangup the call?'
        e.returnValue = confirm
        return confirm
      }
      this.destroySipua()
    }, false)

    window.addEventListener('unload', (e) => {
      this.destroySipua()
    }, false)

    window.addEventListener('message', (e) => {
      if (e.data.eventName === 'click-to-dial') {
        events.pubDialerCallback(e.data.destination)
      }
    })

    // set dark mode
    const isDark = localStorage.getItem('DarkMode')
    this.$vuetify.theme.dark = isDark && isDark === 'true'

    // set favicon
    this.setFavicon()
  },
  computed: {
    ...mapGetters({
      callPanelInitialized: 'callPanelInitialized',
      pendingInitAfterRefresh: 'pendingInitAfterRefresh',
      canReadConfig: 'permissions/confRead',
      RMQConfig: 'settings/rmq',
      sipuaVmNum: 'sipuaVmNum',
      totalUsers: 'users/totalUsers',
      isAuthenticated: 'isAuthenticated',
      isOnCall: 'sipua/isOnCall',
      conferenceActiveRoom: 'conferences/activeRoom',
      videoConferenceActiveRoom: 'videoconfer/activeRoom',
      displayCopyright: 'branding/displayCopyright',
      canReadUsers: 'permissions/usersRead',
      canReadQueues: 'permissions/queuesRead',
      canReadQueueReports: 'permissions/queueReportsRead',
      canReadVideoConf: 'permissions/videoConfRead',
      canReadConferences: 'permissions/conferencesRead',
      canReadParking: 'permissions/parkRead',
      canReadVMails: 'permissions/vmailsRead',
      canChatsRead: 'permissions/chatsRead',
      canReadCdrs: 'permissions/cdrsRead',
      canContactsRead: 'permissions/contactsRead',
      canContactsManagePersonal: 'permissions/contactsManagePersonal',
      canAnnouncementRead: 'permissions/announceRead'
    }),
    displayToolbar () {
      return this.isAuthenticated && !this.embedded
    },
    displayFooter () {
      return this.isAuthenticated && !this.embedded
    },
    displayDialer () {
      return this.isAuthenticated && !this.embedded
    },
    displayEmbeddedToolbar () {
      return this.isAuthenticated && this.embedded && !this.$route.path.match('chat')
    }
  },
  watch: {
    pendingInitAfterRefresh (pending) {
      if (pending && !this.callPanelInitialized) {
        this.setPendingInitAfterRefresh(false)
        this.initAPI()
      }
    }
  },
  methods: {
    ...mapActions({
      initCallsLog: 'initCallsLog',
      reloadVmail: 'reloadVmail',
      initBuddies: 'users/initBuddies',
      setPendingInitAfterRefresh: 'setPendingInitAfterRefresh'
    }),
    initAPI: function () {
      if (!Session.isValid() || this.callPanelInitialized) {
        return // data is loaded. Avoid load on login
      }
      // function in mixins
      this.initCallpanel().then(() => {
        this.routeSetup()
        this.initCallsLog()
      }).catch(e => {
        this.dbg(`Failed initCallpanel: ${e}`)
      })
    },
    // called by unload, cant have promise in there
    // vuex store wrap getter and actions in a promise
    destroySipua () {
      sipua.SIPUA.destroyOnUnload()
    },
    routeSetup: function () {
      // create list of available routes
      const availRoutes = this.getAvailableRoutes()

      if (['login', 'oauth2', 'unsupported', 'embedded_login', '403'].includes(this.$route.name)) {
        this.dbg(`ignore route setup for service route '${this.$route.name}' to avoid loops`)
        return
      }

      if (availRoutes.includes(this.$route.name)) {
        this.dbg(`menu route '${this.$route.name}' is accessible`)
        return
      }

      const path = availRoutes.shift()
      this.$router.push({ name: path }).catch(err => {
        this.dbg(`route push to ${path} failed: ${err}`)
      })
    },
    getAvailableRoutes: function () {
      const routes = []
      const list = {
        announcements: 'canAnnouncementRead',
        users: 'canReadUsers',
        chats: 'canChatsRead',
        'queues-dashboard': 'canReadQueues',
        'queues-reports': 'canReadQueueReports',
        conferences: 'canReadConferences',
        'video-conferences': 'canReadVideoConf',
        parking: 'canReadParking',
        voicemails: 'canReadVMails',
        cdrs: 'canReadCdrs',
        contacts: ['canContactsRead', 'canContactsManagePersonal']
      }

      for (const [path, perms] of Object.entries(list)) {
        if ((Array.isArray(perms) ? perms : [perms]).filter((perm) => !this[perm]).length === 0) {
          routes.push(path)
        }
      }

      // settings
      routes.push('settings-phone-status')
      routes.push('settings-devices')
      routes.push('settings-account')
      routes.push('settings-softphone')
      routes.push('settings-emergency')
      routes.push('settings-notifications')
      routes.push('settings-click-to-dial')
      routes.push('settings-call-screen-pop')
      routes.push('settings-stun-servers')

      return routes
    },
    setFavicon: async () => {
      document.querySelector('#favicon_32').href = '/clearlyip-icon-32.png'
    },
    // set language on launch from mgm lang cookie
    setUILanguage () {
      const langCookie = VueCookies.get('locale')
      if (langCookie) {
        this.$i18n.locale = langCookie
        LocalStorage.setLocale(langCookie)
      }
    },
    appendJitsiExternalAPI: function () {
      try {
        const jitsi = document.createElement('script')
        const jssrc = new URL(API.baseURL())
        jssrc.pathname = 'external_api.js'
        if (process.env.NODE_ENV === 'production') {
          jssrc.host = `live.${jssrc.host}`
          jitsi.setAttribute('src', jssrc.toString())
        } else {
          jssrc.hostname = 'live.clusterpbx.com'
          jssrc.protocol = 'https'
          jssrc.port = '443'
          jitsi.src = jssrc
        }
        document.body.append(jitsi)
      } catch (err) {
        events.pubError('Failed to create video conferece external API instance.' +
          'Please contact technical support')
        this.dbg('%o', err)
      }
    },
    initWakeLock () {
      this.$watch(
        () => [this.isOnCall, this.conferenceActiveRoom, this.videoConferenceActiveRoom],
        ([isOnCall, conferenceActiveRoom, videoConferenceActiveRoom]) => {
          const activeCall = isOnCall
          const activeConf = conferenceActiveRoom && conferenceActiveRoom.id
          const activeVideoConf = videoConferenceActiveRoom && videoConferenceActiveRoom.id
          if (activeCall || activeConf || activeVideoConf) {
            wakeLock.acquire()
          } else {
            wakeLock.release()
          }
        },
        { immediate: true, deep: true }
      )
    }
  }
}
</script>

<style scoped>
</style>
